import { useState, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, Wallet, Building2, Stethoscope, CheckCircle2 } from "lucide-react";
import { DOCTORS, PAYMENT_METHODS, formatBRL } from "@/data/mockData";

export const PaymentDrawer = ({ appointment, onClose, onConfirm }) => {
  const [method, setMethod] = useState("Pix");
  const [received, setReceived] = useState(0);
  const [splitClinica, setSplitClinica] = useState(70);

  useEffect(() => {
    if (appointment) {
      setMethod("Pix");
      setReceived(appointment.value);
      setSplitClinica(appointment.splitClinica ?? 70);
    }
  }, [appointment]);

  const open = Boolean(appointment);
  const doctor = appointment ? DOCTORS.find((d) => d.id === appointment.doctorId) : null;
  const repasseClinica = (received * splitClinica) / 100;
  const repasseMedico = received - repasseClinica;

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            onClick={onClose}
            className="fixed inset-0 z-40 bg-black/20 backdrop-blur-sm"
            data-testid="payment-backdrop"
          />
          <motion.aside
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", stiffness: 320, damping: 34 }}
            className="fixed right-0 top-0 z-50 flex h-full w-full max-w-md flex-col bg-white shadow-2xl"
            data-testid="payment-drawer"
          >
            {/* Header */}
            <div className="flex items-start justify-between border-b border-slate-100 px-6 py-5">
              <div className="flex items-start gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-50 ring-1 ring-blue-100">
                  <Wallet className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-blue-600">
                    Lançamento Relâmpago de Caixa
                  </p>
                  <h2 className="font-heading text-lg font-bold text-slate-900">
                    {appointment?.patient}
                  </h2>
                  <p className="text-sm text-slate-500">
                    {appointment?.procedure} · valor base{" "}
                    <span className="font-mono-num font-semibold text-slate-700">
                      {formatBRL(appointment?.value)}
                    </span>
                  </p>
                </div>
              </div>
              <button
                data-testid="close-payment-drawer"
                onClick={onClose}
                className="flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 transition-colors duration-200 hover:bg-slate-100 hover:text-slate-700"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {/* Body */}
            <div className="flex-1 space-y-7 overflow-y-auto px-6 py-6">
              {/* Forma de pagamento */}
              <div>
                <p className="mb-2.5 text-xs font-semibold uppercase tracking-wide text-slate-400">
                  Forma de Pagamento
                </p>
                <div className="grid grid-cols-2 gap-2.5">
                  {PAYMENT_METHODS.map((m) => {
                    const isActive = method === m;
                    return (
                      <button
                        key={m}
                        data-testid={`payment-method-${m.toLowerCase()}`}
                        onClick={() => setMethod(m)}
                        className={`rounded-xl border px-3 py-3 text-sm font-semibold transition-colors duration-200 ${
                          isActive
                            ? "border-blue-600 bg-blue-50 text-blue-700 ring-1 ring-blue-600"
                            : "border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:bg-slate-50"
                        }`}
                      >
                        {m}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Valor recebido */}
              <div>
                <p className="mb-2.5 text-xs font-semibold uppercase tracking-wide text-slate-400">
                  Valor Recebido
                </p>
                <div className="flex items-center rounded-xl border border-slate-200 bg-slate-50/60 px-4 focus-within:border-blue-600 focus-within:ring-1 focus-within:ring-blue-600">
                  <span className="font-mono-num text-lg font-semibold text-slate-400">R$</span>
                  <input
                    data-testid="payment-value-input"
                    type="number"
                    min="0"
                    value={received}
                    onChange={(e) => setReceived(Number(e.target.value))}
                    className="font-mono-num w-full bg-transparent px-2 py-3.5 text-2xl font-bold text-slate-900 outline-none"
                  />
                </div>
              </div>

              {/* Simulador de repasse */}
              <div className="rounded-2xl border border-slate-200/70 bg-slate-50/70 p-5">
                <div className="mb-4 flex items-center justify-between">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Simulador de Repasse
                  </p>
                  <span className="font-mono-num rounded-md bg-white px-2 py-0.5 text-xs font-bold text-slate-600 ring-1 ring-slate-200">
                    {splitClinica}% / {100 - splitClinica}%
                  </span>
                </div>

                <input
                  data-testid="split-slider"
                  type="range"
                  min="0"
                  max="100"
                  step="5"
                  value={splitClinica}
                  onChange={(e) => setSplitClinica(Number(e.target.value))}
                  className="mb-5 h-1.5 w-full cursor-pointer appearance-none rounded-full bg-slate-200 accent-blue-600"
                />

                <div className="grid grid-cols-2 gap-3">
                  <div className="rounded-xl border border-slate-200 bg-white p-3.5">
                    <div className="mb-1 flex items-center gap-1.5">
                      <Building2 className="h-3.5 w-3.5 text-blue-600" />
                      <span className="text-[11px] font-semibold text-slate-500">
                        Repasse Clínica
                      </span>
                    </div>
                    <p
                      data-testid="repasse-clinica"
                      className="font-mono-num text-lg font-bold text-slate-900"
                    >
                      {formatBRL(repasseClinica)}
                    </p>
                  </div>
                  <div className="rounded-xl border border-slate-200 bg-white p-3.5">
                    <div className="mb-1 flex items-center gap-1.5">
                      <Stethoscope className="h-3.5 w-3.5 text-emerald-600" />
                      <span className="text-[11px] font-semibold text-slate-500">
                        Repasse Médico
                      </span>
                    </div>
                    <p
                      data-testid="repasse-medico"
                      className="font-mono-num text-lg font-bold text-slate-900"
                    >
                      {formatBRL(repasseMedico)}
                    </p>
                  </div>
                </div>
                {doctor && (
                  <p className="mt-3 text-xs text-slate-400">
                    Repasse referente a <span className="font-medium text-slate-500">{doctor.name}</span>
                  </p>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="border-t border-slate-100 px-6 py-4">
              <button
                data-testid="confirm-payment-btn"
                onClick={() =>
                  onConfirm(appointment.id, {
                    method,
                    received,
                    splitClinica,
                    repasseClinica,
                    repasseMedico,
                  })
                }
                className="flex w-full items-center justify-center gap-2 rounded-xl bg-blue-600 py-3.5 text-sm font-bold text-white shadow-[0_4px_14px_rgba(37,99,235,0.4)] transition-colors duration-200 hover:bg-blue-700"
              >
                <CheckCircle2 className="h-4 w-4" />
                Confirmar e Atualizar Caixa · {formatBRL(received)}
              </button>
            </div>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
};
